import type { HistoryResponse, LeagueBundle, ScoresResponse } from '../../shared/types';

/** A manager's identity, stitched together from every season they appear in. */
export interface ManagerRef {
  user_id: string;
  name: string;
  avatar: string | null;
  active: boolean;
}

export interface HeadToHead {
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
  games: number;
}

export interface Rivalry {
  a: ManagerRef;
  b: ManagerRef;
  /** Record from A's perspective. */
  record: HeadToHead;
  /** Total combined margin, used to rank "closest" rivalries. */
  avgMargin: number;
}

const empty = (): HeadToHead => ({
  wins: 0,
  losses: 0,
  ties: 0,
  pointsFor: 0,
  pointsAgainst: 0,
  games: 0,
});

/**
 * Builds the directory of everyone who has ever managed a team.
 *
 * Names come from the current season where possible so a manager who renamed
 * themselves shows their current handle, with historic seasons filling in
 * anyone who has since left the league.
 */
export function managerDirectory(
  bundle: LeagueBundle,
  history: HistoryResponse | null,
): Map<string, ManagerRef> {
  const dir = new Map<string, ManagerRef>();

  for (const season of history?.seasons ?? []) {
    for (const p of season.placements) {
      if (!p.owner_id) continue;
      dir.set(p.owner_id, {
        user_id: p.owner_id,
        name: p.display_name,
        avatar: p.avatar,
        active: false,
      });
    }
  }

  // Current managers win on name/avatar and are flagged active.
  for (const m of bundle.managers) {
    dir.set(m.user_id, {
      user_id: m.user_id,
      name: m.display_name,
      avatar: m.avatar,
      active: true,
    });
  }

  return dir;
}

const key = (a: string, b: string) => `${a}|${b}`;

/**
 * Tallies every head-to-head meeting across all supplied seasons.
 *
 * Records are keyed by **user_id**, never roster_id: Sleeper mints a new league
 * (and a fresh set of roster ids) each season, so a roster-keyed tally would
 * silently merge different managers' histories the first time roster ids
 * shifted between seasons.
 */
export function buildRivalries(
  seasons: ScoresResponse[],
  includePlayoffs: boolean,
): Map<string, HeadToHead> {
  const table = new Map<string, HeadToHead>();

  const bump = (from: string, to: string, pf: number, pa: number) => {
    const rec = table.get(key(from, to)) ?? empty();
    rec.games += 1;
    rec.pointsFor += pf;
    rec.pointsAgainst += pa;
    if (pf > pa) rec.wins += 1;
    else if (pf < pa) rec.losses += 1;
    else rec.ties += 1;
    table.set(key(from, to), rec);
  };

  for (const season of seasons) {
    for (const g of season.games) {
      if (!includePlayoffs && g.isPlayoff) continue;
      const userA = season.rosterOwners[String(g.rosterA)];
      const userB = season.rosterOwners[String(g.rosterB)];
      // An abandoned team can have a null owner; it has no rivalry history.
      if (!userA || !userB || userA === userB) continue;
      bump(userA, userB, g.pointsA, g.pointsB);
      bump(userB, userA, g.pointsB, g.pointsA);
    }
  }

  return table;
}

export function lookup(
  table: Map<string, HeadToHead>,
  a: string,
  b: string,
): HeadToHead | null {
  return table.get(key(a, b)) ?? null;
}

/** One row per unique pairing (not both directions), for the ranked lists. */
export function uniquePairings(
  table: Map<string, HeadToHead>,
  dir: Map<string, ManagerRef>,
): Rivalry[] {
  const seen = new Set<string>();
  const out: Rivalry[] = [];

  for (const [k, record] of table) {
    const [aId, bId] = k.split('|');
    const pairKey = [aId, bId].sort().join('|');
    if (seen.has(pairKey)) continue;
    seen.add(pairKey);

    const a = dir.get(aId);
    const b = dir.get(bId);
    if (!a || !b || record.games === 0) continue;

    out.push({
      a,
      b,
      record,
      avgMargin: Math.abs(record.pointsFor - record.pointsAgainst) / record.games,
    });
  }

  return out;
}

/** Aggregate record across every opponent — a manager's all-time W-L. */
export function overallRecord(table: Map<string, HeadToHead>, userId: string): HeadToHead {
  const total = empty();
  for (const [k, rec] of table) {
    if (!k.startsWith(`${userId}|`)) continue;
    total.wins += rec.wins;
    total.losses += rec.losses;
    total.ties += rec.ties;
    total.pointsFor += rec.pointsFor;
    total.pointsAgainst += rec.pointsAgainst;
    total.games += rec.games;
  }
  return total;
}
